import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navigation from '../shared/components/Navigation';
import Footer from '../shared/components/Footer';
import { dataService } from '../services/dataService';
import './CustomerOrderTrack.css';

const STATUS_DEFS = [
  { key: 'pending', label: 'Order Placed', icon: '🧾' },
  { key: 'preparing', label: 'Preparing', icon: '👨‍🍳' },
  { key: 'ready', label: 'Ready for Pickup', icon: '✅' },
  { key: 'waiting_for_rider', label: 'Waiting for Rider', icon: '⏳' },
  { key: 'rider_assigned', label: 'Rider Assigned', icon: '🏍️' },
  { key: 'picked_up', label: 'Picked Up', icon: '📦' },
  { key: 'enroute', label: 'On the Way', icon: '🛵' },
  { key: 'delivered', label: 'Delivered', icon: '📬' }
];

const CustomerOrderTrack = () => {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let subscription;
    const load = async () => {
      try {
        setLoading(true);
        const data = await dataService.getOrderById(id);
        setOrder(data);
        subscription = dataService.subscribeToOrder(id, async () => {
          try {
            const fresh = await dataService.getOrderById(id);
            setOrder(fresh);
          } catch (e) {
            console.warn('Realtime refresh failed:', e?.message || e);
          }
        });
      } catch (e) {
        setError('Failed to load order');
      } finally {
        setLoading(false);
      }
    };
    load();
    return () => {
      try { subscription?.unsubscribe?.(); } catch (_) {}
    };
  }, [id]);

  const steps = useMemo(() => STATUS_DEFS, []);

  const formatTime = (ts) => ts ? new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—';

  const getTimestamp = (key) => {
    // Prefer structured timestamps from status_timestamps JSON if present
    const map = order?.status_timestamps || null; // optional JSONB in DB
    if (map && map[key]) return map[key];
    // Fallbacks
    if (key === 'pending') return order?.created_at;
    if (key === order?.status) return order?.updated_at;
    return null;
  };

  const renderStep = (step) => {
    const currentIndex = steps.findIndex(s => s.key === (order?.status || 'pending'));
    const stepIndex = steps.findIndex(s => s.key === step.key);
    const done = currentIndex > stepIndex;
    const active = currentIndex === stepIndex;
    const ts = getTimestamp(step.key);
    return (
      <div className={`track-step ${done ? 'done' : active ? 'active' : ''}`} key={step.key}>
        <div className="step-icon" aria-hidden>{step.icon}</div>
        <div className="step-meta">
          <div className="step-title">{step.label}</div>
          <div className="step-time">{formatTime(ts)}</div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="App">
        <Navigation websiteType="customer" />
        <div className="orders-container"><div className="orders-content">Loading...</div></div>
        <Footer websiteType="customer" />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="App">
        <Navigation websiteType="customer" />
        <div className="orders-container"><div className="orders-content">{error || 'Order not found'}</div></div>
        <Footer websiteType="customer" />
      </div>
    );
  }

  return (
    <div className="App">
      <Navigation websiteType="customer" />
      <div className="orders-container">
        <div className="orders-content">
          <div className="orders-header">
            <h1 className="orders-title">Track Order #{order.id}</h1>
            <p className="orders-subtitle">Status: {order.status.replaceAll('_',' ')}</p>
          </div>
          <div className="track-card">
            <div className="track-progress">
              <div className="track-rail" />
              <div className="track-steps">
                {steps.map(renderStep)}
              </div>
            </div>
          </div>
          <div style={{ marginTop: 16 }}>
            <Link to="/orders" className="browse-menu-btn">Back to Orders</Link>
          </div>
        </div>
      </div>
      <Footer websiteType="customer" />
    </div>
  );
};

export default CustomerOrderTrack;


